package com.evoapps.evoshift.widgets

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.WarningAmber
import androidx.compose.foundation.layout.size
import com.evoapps.evoshift.ui.WarningEdge
import com.evoapps.evoshift.ui.WarningSurface
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import com.evoapps.evoshift.ui.theme.motionContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.CardMetallicBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.CardHairline
import com.evoapps.evoshift.ui.CardSurface
import com.evoapps.evoshift.ui.adaptive.CardTint
import com.evoapps.evoshift.ui.adaptive.focusRing
import com.evoapps.evoshift.ui.adaptive.handCursor

@Composable
fun LoadingOverlay(isLoading: Boolean) {
    if (isLoading) {
        Box(
            modifier = Modifier
                .fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            CircularProgressIndicator(color = MaterialTheme.colorScheme.secondary)
        }
    }
}

/**
 * A "Standout" card used for primary dashboard items (e.g., Next Shift).
 * Features a dark navy metallic background and gold border.
 */
@Composable
fun StandoutCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val outerShape = RoundedCornerShape(24.dp)
    val innerShape = RoundedCornerShape(18.dp)
    
    val colors = CardDefaults.cardColors(
        containerColor = Color.Transparent,
        contentColor = Color.White
    )
    val outerBorder = BorderStroke(1.dp, GoldMetallicBrush)
    val elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)

    val inner: @Composable ColumnScope.() -> Unit = {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(CardMetallicBrush) // Dark Navy Metallic
                .padding(6.dp)
                .border(1.dp, Color.White.copy(alpha = 0.2f), innerShape)
                .clip(innerShape)
        ) {
            // Light text and the dialogs' colours on the navy card (a disabled card's faded default otherwise).
            ProvideDialogColors {
                Column(modifier = Modifier.fillMaxWidth()) {
                    content()
                }
            }
        }
    }
    if (onClick == null) {
        // Not clickable: a disabled click keeps the card one merged node for screen readers and
        // tests, as it always was.
        Card(onClick = {}, enabled = false, modifier = modifier, shape = outerShape, colors = colors, border = outerBorder, elevation = elevation, content = inner)
    } else {
        Card(
            modifier = modifier.cardClick(onClick, outerShape, 8.dp, Color.White),
            shape = outerShape,
            colors = colors,
            border = outerBorder,
            elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
            content = inner
        )
    }
}

/**
 * A "Regular" card used for secondary information.
 * SH-230: a clean white surface with a hairline edge and a soft shadow, so it stands off the
 * cream page (before: a cream gradient that blended into the page and looked washed out).
 */
@Composable
fun RegularCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val outerShape = RoundedCornerShape(20.dp)
    val innerShape = RoundedCornerShape(14.dp)
    
    val colors = CardDefaults.cardColors(
        containerColor = Color.Transparent,
        contentColor = DeepNavy
    )

    val outerBorder = BorderStroke(1.dp, CardHairline)
    val elevation = CardDefaults.cardElevation(defaultElevation = RegularCardElevation)

    val inner: @Composable ColumnScope.() -> Unit = {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(CardSurface)
                // The inset the cream metallic frame had, so the content keeps its place.
                .padding(5.dp)
                .clip(innerShape)
        ) {
            // Navy text and the screen's colours on the cream card, inside a navy dialog or sheet too.
            ProvideScreenColors {
                Column(modifier = Modifier.fillMaxWidth()) {
                    content()
                }
            }
        }
    }
    if (onClick == null) {
        // Not clickable: a disabled click keeps the card one merged node for screen readers and
        // tests, as it always was.
        Card(onClick = {}, enabled = false, modifier = modifier, shape = outerShape, colors = colors, border = outerBorder, elevation = elevation, content = inner)
    } else {
        Card(
            modifier = modifier.cardClick(onClick, outerShape, RegularCardElevation, DeepNavy),
            shape = outerShape,
            colors = colors,
            border = outerBorder,
            elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
            content = inner
        )
    }
}

/** SH-230: a soft shadow; the hairline edge does the separating. */
private val RegularCardElevation = 2.dp

/**
 * SH-230: the screens' plain information card (a course, a document, a policy, a cost): the
 * [RegularCard]'s white surface and hairline edge as a Material card, for content with its own
 * buttons. Before, these were Material cards in surfaceVariant: pale cream on the cream page.
 * [warning] gives it the amber edge of something that still needs doing.
 */
@Composable
fun SurfaceCard(
    modifier: Modifier = Modifier,
    warning: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = CardSurface, contentColor = DeepNavy),
        border = BorderStroke(if (warning) 1.5.dp else 1.dp, if (warning) WarningEdge else CardHairline),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        content = content
    )
}

/**
 * SH-230: a "still to do" line in a card: an amber strip with a warning icon, so a warning reads
 * as one whatever the card's colour (e.g. "the initial OHS training is not complete yet").
 */
@Composable
fun WarningNote(text: String, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(WarningSurface)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(Icons.Default.WarningAmber, contentDescription = null, tint = OnScreen.warning, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
        Text(text, style = MaterialTheme.typography.bodyMedium, color = OnScreen.warning, fontWeight = FontWeight.Medium)
    }
}

/**
 * SH-130: a card's click, with the [CardTint] over the whole card instead of a ripple. The card's
 * shadow is drawn here (the Card itself then has none) because the layer clips its content to the
 * card's shape: hover and press tint the card exactly, corners included, with no inner rectangle.
 */
private fun Modifier.cardClick(onClick: () -> Unit, shape: Shape, elevation: Dp, tint: Color): Modifier =
    // SH-209: the keyboard focus ring outside the card, so before the clipping shadow.
    focusRing(shape)
        .shadow(elevation, shape, clip = true)
        .handCursor()
        .clickable(interactionSource = null, indication = CardTint(tint), onClick = onClick)

/**
 * SH-213: a settings-style menu row (the profile's documents, calendar, notifications, language
 * and the like): the team list's [RegularCard] (its white surface, hairline edge and shadow,
 * the hover tint and the keyboard focus ring), the icon, the [title], an optional line under it
 * and a chevron. Before, these were Material cards in surfaceVariant: pale cream on the cream
 * screen with no visible edge.
 */
@Composable
fun MenuRowCard(
    icon: ImageVector,
    title: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    trailingIcon: ImageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight
) {
    RegularCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Row(modifier = Modifier.fillMaxWidth().padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
            Spacer(Modifier.width(12.dp))
            // A line that arrives later (e.g. "Set" once known) unfolds instead of pushing the card open.
            Column(modifier = Modifier.weight(1f).motionContentSize()) {
                Text(title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Medium)
                subtitle?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            Icon(trailingIcon, contentDescription = null)
        }
    }
}
